<template>
  <div class="fichaje-admin-v2">

    <!-- ============ DASHBOARD HEADER ============ -->
    <div class="fa-dash-header">
      <div class="fa-eyebrow">CLOCK · TIME TRACKING</div>
      <h2 class="fa-title">Fichajes y faltas</h2>
    </div>

    <b-card no-body>
      <b-tabs card>
        <!-- ============================================ -->
        <!-- TAB 1: FICHAJES (contenido original) -->
        <!-- ============================================ -->
        <b-tab title="Fichajes" active>
          <!-- Filtros -->
          <b-row class="mb-3">
            <b-col md="3">
              <b-form-group label="Usuario">
                <v-select
                  v-model="filtroUsuario"
                  :options="usuarios"
                  label="nombre"
                  :reduce="u => u.id"
                  placeholder="Todos los usuarios"
                  :clearable="true"
                  @input="cargarFichajes"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Desde">
                <flat-pickr
                  v-model="filtroDesde"
                  class="date-input"
                  @on-change="cargarFichajes"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Hasta">
                <flat-pickr
                  v-model="filtroHasta"
                  class="date-input"
                  @on-change="cargarFichajes"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" class="d-flex align-items-end flex-wrap">
              <b-button variant="primary" @click="abrirCrearFichaje" class="mb-1 mr-2">
                <i class="fa-solid fa-plus mr-1"></i> Nuevo
              </b-button>
              <b-button variant="outline-danger" @click="abrirModalPDF" class="mb-1 mr-2">
                <i class="fa-solid fa-file-pdf mr-1"></i> PDF
              </b-button>
              <b-button variant="outline-secondary" @click="limpiarFiltros" class="mb-1">
                <i class="fa-solid fa-filter-circle-xmark mr-1"></i>
              </b-button>
            </b-col>
          </b-row>

          <!-- Resumen -->
          <div class="fa-kpi-strip mb-3">
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">DÍAS TRABAJADOS</div>
              <div class="fa-kpi-value">{{ fichajosAgrupados.length }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-blue"></span><span>en el periodo</span></div>
            </div>
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">TOTAL FICHAJES</div>
              <div class="fa-kpi-value">{{ fichajes.length }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-cyan"></span><span>entradas / salidas</span></div>
            </div>
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">HORAS TRABAJADAS</div>
              <div class="fa-kpi-value">{{ totalHorasPeriodo }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-green pulse"></span><span>acumuladas</span></div>
            </div>
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">FICHAJES ABIERTOS</div>
              <div class="fa-kpi-value">{{ fichajesAbiertos }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-orange"></span><span>sin cerrar</span></div>
            </div>
          </div>

          <!-- Fichajes del día actual -->
          <div v-if="fichajosHoy.length > 0" class="mb-4">
            <h5 class="mb-2"><i class="fa-solid fa-calendar-day mr-1"></i> Hoy</h5>
            <b-table
              :items="fichajosHoy"
              :fields="columnas"
              responsive
              striped
              hover
              class="tabla-hoy"
            >
              <template #cell(usuario)="data">
                <strong>{{ data.item.usuario_nombre }}</strong>
              </template>
              <template #cell(fecha)="data">
                {{ formatFecha(data.item.primera_entrada) }}
              </template>
              <template #cell(primera_entrada)="data">
                {{ formatHora(data.item.primera_entrada) }}
                <i
                  v-if="iconoTipo(data.item.tipo_primera_entrada)"
                  :class="'fa-solid ' + iconoTipo(data.item.tipo_primera_entrada)"
                  :style="{ color: colorTipo(data.item.tipo_primera_entrada) }"
                  :title="data.item.tipo_primera_entrada"
                  class="ml-1"
                ></i>
              </template>
              <template #cell(ultima_salida)="data">
                <span v-if="data.item.ultima_salida && !data.item.tiene_abierto">
                  {{ formatHora(data.item.ultima_salida) }}
                  <i
                    v-if="iconoTipo(data.item.tipo_ultima_salida)"
                    :class="'fa-solid ' + iconoTipo(data.item.tipo_ultima_salida)"
                    :style="{ color: colorTipo(data.item.tipo_ultima_salida) }"
                    :title="data.item.tipo_ultima_salida"
                    class="ml-1"
                  ></i>
                </span>
                <b-badge v-else variant="warning">Abierto</b-badge>
              </template>
              <template #cell(fichajes_dia)="data">
                <b-badge variant="light-primary">{{ data.item.fichajes.length }}</b-badge>
              </template>
              <template #cell(total)="data">
                {{ calcularTotalHorasAgrupado(data.item) }}
              </template>
              <template #cell(horas_extras)="data">
                <b-badge v-if="calcularHorasExtrasAgrupado(data.item)" :variant="esHoraExtraSignificativa(data.item) ? 'danger' : 'secondary'">
                  {{ calcularHorasExtrasAgrupado(data.item) }}
                </b-badge>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(descanso)="data">
                {{ calcularDescanso(data.item) }}
              </template>
              <template #cell(editado)="data">
                <b-badge v-if="data.item.tiene_editado" variant="light-warning" title="Algun fichaje fue editado">
                  <i class="fa-solid fa-pen-to-square mr-25"></i> Editado
                </b-badge>
              </template>
              <template #cell(acciones)="data">
                <b-button
                  variant="outline-info"
                  size="sm"
                  @click="abrirDetalleDia(data.item)"
                  title="Ver detalle del dia"
                >
                  <i class="fa-solid fa-eye"></i>
                </b-button>
              </template>
            </b-table>
          </div>

          <!-- Historial de fichajes -->
          <h5 v-if="fichajosHoy.length > 0" class="mb-2"><i class="fa-solid fa-clock-rotate-left mr-1"></i> Historial</h5>
          <b-table
            :items="fichajosHistorial"
            :fields="columnas"
            responsive
            striped
            hover
            show-empty
            empty-text="No hay fichajes en este periodo"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
          >
            <template #cell(usuario)="data">
              <strong>{{ data.item.usuario_nombre }}</strong>
            </template>
            <template #cell(fecha)="data">
              {{ formatFecha(data.item.primera_entrada) }}
            </template>
            <template #cell(primera_entrada)="data">
              {{ formatHora(data.item.primera_entrada) }}
              <i
                v-if="iconoTipo(data.item.tipo_primera_entrada)"
                :class="'fa-solid ' + iconoTipo(data.item.tipo_primera_entrada)"
                :style="{ color: colorTipo(data.item.tipo_primera_entrada) }"
                :title="data.item.tipo_primera_entrada"
                class="ml-1"
              ></i>
            </template>
            <template #cell(ultima_salida)="data">
              <span v-if="data.item.ultima_salida && !data.item.tiene_abierto">
                {{ formatHora(data.item.ultima_salida) }}
                <i
                  v-if="iconoTipo(data.item.tipo_ultima_salida)"
                  :class="'fa-solid ' + iconoTipo(data.item.tipo_ultima_salida)"
                  :style="{ color: colorTipo(data.item.tipo_ultima_salida) }"
                  :title="data.item.tipo_ultima_salida"
                  class="ml-1"
                ></i>
              </span>
              <b-badge v-else variant="warning">Abierto</b-badge>
            </template>
            <template #cell(fichajes_dia)="data">
              <b-badge variant="light-primary">{{ data.item.fichajes.length }}</b-badge>
            </template>
            <template #cell(total)="data">
              {{ calcularTotalHorasAgrupado(data.item) }}
            </template>
            <template #cell(horas_extras)="data">
              <b-badge v-if="calcularHorasExtrasAgrupado(data.item)" :variant="esHoraExtraSignificativa(data.item) ? 'danger' : 'secondary'">
                {{ calcularHorasExtrasAgrupado(data.item) }}
              </b-badge>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(descanso)="data">
              {{ calcularDescanso(data.item) }}
            </template>
            <template #cell(editado)="data">
              <b-badge v-if="data.item.tiene_editado" variant="light-warning" title="Algun fichaje fue editado">
                <i class="fa-solid fa-pen-to-square mr-25"></i> Editado
              </b-badge>
            </template>
            <template #cell(acciones)="data">
              <b-button
                variant="outline-info"
                size="sm"
                @click="abrirDetalleDia(data.item)"
                title="Ver detalle del dia"
              >
                <i class="fa-solid fa-eye"></i>
              </b-button>
            </template>
          </b-table>

          <!-- Resumen por usuario -->
          <h5 class="mt-4 mb-2">Resumen por usuario</h5>
          <b-table
            :items="resumenPorUsuario"
            :fields="columnasResumen"
            responsive
            striped
            hover
            :tbody-tr-class="rowClassResumen"
          >
            <template #cell(total_horas)="data">
              {{ data.item.total_horas }}
            </template>
            <template #cell(horas_extras)="data">
              <span v-if="data.item.minutos_extras > 0">
                {{ data.item.horas_extras }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(acciones)="data">
              <b-button
                v-if="data.item.dias_extras && data.item.dias_extras.length > 0"
                variant="outline-danger"
                size="sm"
                @click="abrirDetalleHorasExtras(data.item)"
                title="Ver dias con horas extras"
              >
                <i class="fa-solid fa-clock"></i>
              </b-button>
            </template>
          </b-table>
        </b-tab>

        <!-- ============================================ -->
        <!-- TAB 2: FALTAS -->
        <!-- ============================================ -->
        <b-tab @click="onTabFaltas">
          <template #title>
            Faltas
            <b-badge v-if="faltasPendientes.length > 0" variant="danger" class="ml-50">{{ faltasPendientes.length }}</b-badge>
          </template>
          <!-- Filtros faltas -->
          <b-row class="mb-3">
            <b-col md="3">
              <b-form-group label="Usuario">
                <v-select
                  v-model="faltasFiltroUsuario"
                  :options="usuariosConLlave"
                  label="nombre"
                  :reduce="u => u.id"
                  placeholder="Todos los usuarios"
                  :clearable="true"
                  @input="detectarFaltas"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Desde">
                <flat-pickr
                  v-model="faltasFiltroDesde"
                  class="date-input"
                  @on-change="detectarFaltas"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Hasta">
                <flat-pickr
                  v-model="faltasFiltroHasta"
                  class="date-input"
                  @on-change="detectarFaltas"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Estado">
                <v-select
                  v-model="faltasFiltroEstado"
                  :options="opcionesEstadoFalta"
                  label="label"
                  :reduce="o => o.value"
                  :clearable="false"
                  placeholder="Todas"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" class="d-flex align-items-end" v-if="false">
              <b-button variant="primary" @click="detectarFaltas" class="mb-1" :disabled="faltasLoading">
                <i class="fa-solid fa-magnifying-glass mr-1"></i>
                {{ faltasLoading ? 'Detectando...' : 'Detectar Faltas' }}
              </b-button>
            </b-col>
          </b-row>

          <!-- Resumen faltas -->
          <div class="fa-kpi-strip fa-kpi-strip-5 mb-3" v-if="faltasDetectadas.length > 0">
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">TOTAL</div>
              <div class="fa-kpi-value">{{ faltasDetectadas.length }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-cyan"></span><span>detectadas</span></div>
            </div>
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">PENDIENTES</div>
              <div class="fa-kpi-value">{{ faltasPendientes.length }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-orange pulse"></span><span>por revisar</span></div>
            </div>
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">JUSTIFICADAS</div>
              <div class="fa-kpi-value">{{ faltasJustificadas.length }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-green"></span><span>aprobadas</span></div>
            </div>
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">INJUSTIFICADAS</div>
              <div class="fa-kpi-value">{{ faltasInjustificadas.length }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-red"></span><span>denegadas</span></div>
            </div>
            <div class="fa-kpi-card">
              <div class="fa-kpi-label">OMITIDAS</div>
              <div class="fa-kpi-value">{{ faltasOmitidas.length }}</div>
              <div class="fa-kpi-foot"><span class="fa-dot fa-dot-grey"></span><span>ignoradas</span></div>
            </div>
          </div>

          <!-- Tabla faltas -->
          <b-table
            :items="faltasFiltradas"
            :fields="columnasFaltas"
            responsive
            striped
            hover
            show-empty
            empty-text="No se han detectado faltas en este periodo"
          >
            <template #cell(usuario_nombre)="data">
              <strong>{{ data.item.usuario_nombre }}</strong>
            </template>
            <template #cell(fecha)="data">
              {{ formatFecha(data.item.fecha) }}
            </template>
            <template #cell(tipo)="data">
              <b-badge :variant="data.item.tipo === 'dia_completo' ? 'danger' : 'warning'">
                {{ data.item.tipo === 'dia_completo' ? 'Dia completo' : 'Parcial' }}
              </b-badge>
            </template>
            <template #cell(horas)="data">
              {{ formatDecimalAHoras(data.item.horas_trabajadas) }} / {{ formatDecimalAHoras(data.item.horas_esperadas) }}
            </template>
            <template #cell(estado)="data">
              <b-badge :variant="variantEstadoFalta(data.item.estado)">
                {{ data.item.estado }}
              </b-badge>
              <small v-if="data.item.categoria_nombre" class="d-block text-muted">
                {{ data.item.categoria_nombre }}
              </small>
            </template>
            <template #cell(acciones)="data">
              <b-button
                v-if="data.item.estado !== 'justificada'"
                variant="outline-success"
                size="sm"
                @click="abrirJustificarFalta(data.item)"
                title="Justificar"
                class="mr-50"
              >
                <i class="fa-solid fa-check"></i>
              </b-button>
              <b-button
                v-if="data.item.estado !== 'injustificada'"
                variant="outline-danger"
                size="sm"
                @click="marcarInjustificada(data.item)"
                title="Injustificada"
                class="mr-50"
              >
                <i class="fa-solid fa-xmark"></i>
              </b-button>
              <b-button
                v-if="data.item.estado !== 'omitida'"
                variant="outline-secondary"
                size="sm"
                @click="marcarOmitida(data.item)"
                title="Omitir"
                class="mr-50"
              >
                <i class="fa-solid fa-eye-slash"></i>
              </b-button>
              <b-button
                v-if="data.item.estado !== 'pendiente'"
                variant="outline-warning"
                size="sm"
                @click="marcarPendiente(data.item)"
                title="Volver a pendiente"
              >
                <i class="fa-solid fa-rotate-left"></i>
              </b-button>
            </template>
          </b-table>

          <!-- Resumen anual por usuario -->
          <div class="resumen-anual-section mt-4">
            <div class="resumen-anual-header">
              <div class="d-flex align-items-center">
                <i class="fa-solid fa-chart-bar resumen-anual-icon"></i>
                <div>
                  <h5 class="mb-0">Resumen anual por usuario</h5>
                  <small class="text-muted">Total de faltas acumuladas en el año</small>
                </div>
              </div>
              <b-button-group size="sm">
                <b-button
                  :variant="resumenAnio === anioActual ? 'primary' : 'outline-primary'"
                  @click="cambiarAnioResumen(anioActual)"
                >
                  {{ anioActual }}
                </b-button>
                <b-button
                  :variant="resumenAnio === anioActual - 1 ? 'primary' : 'outline-primary'"
                  @click="cambiarAnioResumen(anioActual - 1)"
                >
                  {{ anioActual - 1 }}
                </b-button>
              </b-button-group>
            </div>
            <b-table
              :items="resumenFaltasAgrupado"
              :fields="columnasResumenFaltas"
              responsive
              striped
              hover
              small
              show-empty
              :empty-text="resumenLoading ? 'Cargando...' : 'Sin faltas en este periodo'"
              :busy="resumenLoading"
            >
              <template #cell(usuario_nombre)="data">
                <strong>{{ data.item.usuario_nombre }}</strong>
              </template>
              <template #cell(total)="data">
                <b-badge variant="light-info">{{ data.item.total }}</b-badge>
              </template>
              <template #cell(dias_completos)="data">
                <b-badge v-if="data.item.dias_completos > 0" variant="danger">{{ data.item.dias_completos }}</b-badge>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(parciales)="data">
                <b-badge v-if="data.item.parciales > 0" variant="warning">{{ data.item.parciales }}</b-badge>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(pendientes)="data">
                <b-badge v-if="data.item.pendientes > 0" variant="warning">{{ data.item.pendientes }}</b-badge>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(justificadas)="data">
                <b-badge v-if="data.item.justificadas > 0" variant="success">{{ data.item.justificadas }}</b-badge>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(injustificadas)="data">
                <b-badge v-if="data.item.injustificadas > 0" variant="danger">{{ data.item.injustificadas }}</b-badge>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(omitidas)="data">
                <b-badge v-if="data.item.omitidas > 0" variant="secondary">{{ data.item.omitidas }}</b-badge>
                <span v-else class="text-muted">-</span>
              </template>
              <template #cell(acciones)="data">
                <b-button size="sm" variant="flat-info" @click="abrirDetalleFaltas(data.item)">
                  <i class="fa-solid fa-eye"></i>
                </b-button>
              </template>
            </b-table>
          </div>
        </b-tab>

        <!-- ============================================ -->
        <!-- TAB 3: HORARIOS -->
        <!-- ============================================ -->
        <b-tab title="Horarios" @click="onTabHorarios">
          <b-row class="mb-3">
            <b-col md="4">
              <b-form-group label="Seleccionar usuario">
                <v-select
                  v-model="horarioUsuarioId"
                  :options="usuariosConLlave"
                  label="nombre"
                  :reduce="u => u.id"
                  placeholder="Seleccionar usuario"
                  append-to-body
                  @input="cargarHorarioUsuario"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <div v-if="horarioUsuarioId">
            <b-table
              :items="horarioEditable"
              :fields="columnasHorario"
              responsive
              striped
            >
              <template #cell(dia)="data">
                <strong>{{ data.item.dia_nombre }}</strong>
              </template>
              <template #cell(hora_entrada)="data">
                <b-form-input
                  type="time"
                  v-model="data.item.hora_entrada"
                  size="sm"
                  :disabled="!data.item.activo"
                />
              </template>
              <template #cell(hora_salida)="data">
                <b-form-input
                  type="time"
                  v-model="data.item.hora_salida"
                  size="sm"
                  :disabled="!data.item.activo"
                />
              </template>
              <template #cell(minutos_descanso)="data">
                <b-form-input
                  type="number"
                  v-model="data.item.minutos_descanso"
                  size="sm"
                  min="0"
                  max="120"
                  :disabled="!data.item.activo"
                />
              </template>
              <template #cell(activo)="data">
                <b-form-checkbox
                  v-model="data.item.activo"
                  switch
                />
              </template>
            </b-table>

            <b-button variant="primary" @click="guardarHorario" :disabled="horarioSaving">
              <i class="fa-solid fa-floppy-disk mr-1"></i>
              {{ horarioSaving ? 'Guardando...' : 'Guardar Horario' }}
            </b-button>
          </div>
          <div v-else class="text-center text-muted py-4">
            Selecciona un usuario para ver y editar su horario laboral
          </div>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Modal Crear Fichaje -->
    <b-modal
      id="modal-crear-fichaje"
      title="Crear Fichaje"
      @ok="guardarNuevoFichaje"
      ok-title="Crear"
      cancel-title="Cancelar"
      :ok-disabled="!nuevoForm.usuario_id || !nuevoForm.fecha || !nuevoForm.hora_entrada"
    >
      <b-form-group label="Usuario *">
        <v-select
          v-model="nuevoForm.usuario_id"
          :options="usuarios"
          label="nombre"
          :reduce="u => u.id"
          placeholder="Seleccionar usuario"
        />
      </b-form-group>
      <b-form-group label="Fecha *">
        <b-form-input type="date" v-model="nuevoForm.fecha" />
      </b-form-group>
      <b-form-group label="Hora Entrada *">
        <b-form-input type="time" v-model="nuevoForm.hora_entrada" />
      </b-form-group>
      <b-form-group label="Hora Salida">
        <b-form-input type="time" v-model="nuevoForm.hora_salida" />
      </b-form-group>
      <b-form-group label="Motivo">
        <b-form-textarea
          v-model="nuevoForm.motivo"
          placeholder="Indica el motivo de crear este fichaje (opcional)..."
          rows="3"
        />
      </b-form-group>
    </b-modal>

    <!-- Modal Cerrar Fichaje -->
    <b-modal
      id="modal-cerrar-fichaje"
      title="Cerrar Fichaje"
      @ok="confirmarCerrarFichaje"
      ok-title="Cerrar Fichaje"
      ok-variant="danger"
      cancel-title="Cancelar"
    >
      <div class="text-center mb-3">
        <i class="fa-solid fa-clock text-warning" style="font-size: 3rem"></i>
      </div>
      <p class="text-center">
        ¿Cerrar el fichaje de <strong>{{ fichajeCerrar ? fichajeCerrar.usuario_nombre : '' }}</strong> con la hora actual?
      </p>
      <p class="text-center text-muted small">
        Entrada: {{ fichajeCerrar ? formatHora(fichajeCerrar.hora_entrada) : '' }}
      </p>
    </b-modal>

    <!-- Modal Eliminar Fichaje -->
    <b-modal
      id="modal-eliminar-fichaje"
      title="Eliminar Fichaje"
      @ok="confirmarEliminarFichaje"
      ok-title="Eliminar"
      ok-variant="danger"
      cancel-title="Cancelar"
    >
      <div class="text-center mb-3">
        <i class="fa-solid fa-trash text-danger" style="font-size: 3rem"></i>
      </div>
      <p class="text-center">
        ¿Eliminar el fichaje de <strong>{{ fichajeEliminar ? fichajeEliminar.usuario_nombre : '' }}</strong>?
      </p>
      <p class="text-center text-muted small">
        Fecha: {{ fichajeEliminar ? formatFecha(fichajeEliminar.hora_entrada) : '' }}<br>
        Entrada: {{ fichajeEliminar ? formatHora(fichajeEliminar.hora_entrada) : '' }}
        <span v-if="fichajeEliminar && fichajeEliminar.hora_salida">
          - Salida: {{ formatHora(fichajeEliminar.hora_salida) }}
        </span>
      </p>
      <p class="text-center text-danger small">
        <i class="fa-solid fa-warning mr-1"></i> Esta accion no se puede deshacer
      </p>
    </b-modal>

    <!-- Modal Editar Fichaje -->
    <b-modal
      id="modal-editar-fichaje-admin"
      title="Editar Fichaje"
      @ok="guardarEdicion"
      ok-title="Guardar"
      cancel-title="Cancelar"
      :ok-disabled="false"
    >
      <b-form-group label="Usuario">
        <b-form-input :value="editForm.usuario_nombre" disabled />
      </b-form-group>
      <b-form-group label="Fecha">
        <b-form-input :value="formatFecha(editForm.fecha)" disabled />
      </b-form-group>
      <b-form-group label="Hora Entrada">
        <b-form-input type="time" v-model="editForm.hora_entrada" />
      </b-form-group>
      <b-form-group label="Hora Salida">
        <b-input-group>
          <b-form-input type="time" v-model="editForm.hora_salida" />
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="editForm.hora_salida = ''" title="Vaciar hora salida">
              <i class="fa-solid fa-xmark"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-form-group label="Motivo del cambio">
        <b-form-textarea
          v-model="editForm.motivo"
          placeholder="Indica el motivo de la modificacion (opcional)..."
          rows="3"
        />
      </b-form-group>
    </b-modal>

    <!-- Modal Detalle del Día -->
    <b-modal
      id="modal-detalle-dia"
      :title="detalleDia ? ('Fichajes de ' + detalleDia.usuario_nombre + ' - ' + formatFecha(detalleDia.primera_entrada)) : 'Detalle del dia'"
      size="lg"
      ok-only
      ok-title="Cerrar"
    >
      <div class="d-flex justify-content-end mb-1">
        <b-button
          size="sm"
          variant="primary"
          @click="abrirCrearFichajeDesdeDetalle"
        >
          <i class="fa-solid fa-plus mr-50"></i> Nuevo fichaje
        </b-button>
      </div>
      <b-table
        v-if="detalleDia"
        :items="detalleDia.fichajes"
        :fields="columnasDetalle"
        responsive
        striped
        small
      >
        <template #cell(hora_entrada)="data">
          {{ formatHora(data.item.hora_entrada) }}
          <i
            v-if="iconoTipo(data.item.tipo_entrada)"
            :class="'fa-solid ' + iconoTipo(data.item.tipo_entrada)"
            :style="{ color: colorTipo(data.item.tipo_entrada) }"
            :title="data.item.tipo_entrada"
            class="ml-1"
          ></i>
        </template>
        <template #cell(hora_salida)="data">
          <span v-if="data.item.hora_salida">
            {{ formatHora(data.item.hora_salida) }}
            <i
              v-if="iconoTipo(data.item.tipo_salida)"
              :class="'fa-solid ' + iconoTipo(data.item.tipo_salida)"
              :style="{ color: colorTipo(data.item.tipo_salida) }"
              :title="data.item.tipo_salida"
              class="ml-1"
            ></i>
          </span>
          <b-badge v-else variant="warning">Abierto</b-badge>
        </template>
        <template #cell(total)="data">
          {{ calcularTotalHoras(data.item) }}
        </template>
        <template #cell(modificado)="data">
          <b-badge v-if="data.item.fecha_modificacion || data.item.modificado_por" variant="light-warning">
            <i class="fa-solid fa-pen-to-square"></i>
          </b-badge>
        </template>
        <template #cell(acciones)="data">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="abrirEditarFichaje(data.item)"
            title="Editar fichaje"
            class="mr-1"
          >
            <i class="fa-solid fa-pen"></i>
          </b-button>
          <b-button
            v-if="!data.item.hora_salida"
            variant="outline-warning"
            size="sm"
            @click="cerrarFichaje(data.item)"
            title="Cerrar fichaje"
            class="mr-1"
          >
            <i class="fa-solid fa-xmark"></i>
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="abrirEliminarFichaje(data.item)"
            title="Eliminar fichaje"
          >
            <i class="fa-solid fa-trash"></i>
          </b-button>
        </template>
        <template #row-details="data">
          <div class="px-2 py-1 bg-light-warning rounded small">
            <i class="fa-solid fa-pen-to-square mr-50"></i>
            <strong>Motivo:</strong> {{ data.item.motivo_modificacion || 'Sin motivo' }}
            <span class="ml-1 text-muted">
              — {{ data.item.modificado_por_nombre || 'Admin' }}, {{ formatFechaHora(data.item.fecha_modificacion) }}
            </span>
          </div>
        </template>
      </b-table>

      <div v-if="detalleDia" class="d-flex justify-content-between mt-2">
        <span v-if="calcularDescanso(detalleDia) !== '-'">
          <strong>Descanso: {{ calcularDescanso(detalleDia) }}</strong>
        </span>
        <span v-else></span>
        <strong>Total trabajado: {{ calcularTotalHorasAgrupado(detalleDia) }}</strong>
      </div>
    </b-modal>

    <!-- Modal Generar PDF -->
    <b-modal
      id="modal-pdf"
      title="Generar PDF Registro de Jornada"
      size="md"
      @ok="generarPDF"
      ok-title="Generar PDF"
      cancel-title="Cancelar"
      :ok-disabled="!pdfForm.mes"
    >
      <b-form-group label="Mes *">
        <b-form-input type="month" v-model="pdfForm.mes" />
      </b-form-group>
      <b-form-group label="Usuario">
        <v-select
          v-model="pdfForm.usuario_id"
          :options="usuariosConSS"
          label="nombre"
          :reduce="u => u.id"
          placeholder="Todos los usuarios (un PDF por pagina)"
          :clearable="true"
        />
      </b-form-group>
      <b-form-group label="Nombre de la empresa">
        <b-form-input
          v-model="pdfForm.empresa"
          placeholder="Ej: CRAFT LAB SL UNIPERSONAL"
        />
      </b-form-group>
      <small class="text-muted">
        <i class="fa-solid fa-info-circle mr-1"></i>
        Si no seleccionas usuario, se generara un PDF con todos los usuarios, cada uno en una pagina separada.
      </small>
    </b-modal>

    <!-- Modal Detalle Horas Extras -->
    <b-modal
      id="modal-horas-extras"
      :title="detalleHorasExtras ? ('Horas extras de ' + detalleHorasExtras.nombre) : 'Horas extras'"
      size="lg"
      ok-only
      ok-title="Cerrar"
    >
      <b-table
        v-if="detalleHorasExtras"
        :items="detalleHorasExtras.dias"
        :fields="columnasHorasExtras"
        responsive
        striped
        small
      >
        <template #cell(fecha)="data">
          {{ formatFecha(data.item.fecha) }}
        </template>
        <template #cell(horas_trabajadas)="data">
          {{ data.item.horas_trabajadas }}
        </template>
        <template #cell(horas_extras)="data">
          <b-badge variant="danger">{{ data.item.horas_extras }}</b-badge>
        </template>
      </b-table>
      <div v-if="detalleHorasExtras" class="text-right mt-2">
        <strong>Total horas extras: {{ detalleHorasExtras.total_extras }}</strong>
      </div>
    </b-modal>

    <!-- Modal Justificar Falta -->
    <b-modal
      id="modal-justificar-falta"
      title="Justificar Falta"
      @ok="confirmarJustificarFalta"
      ok-title="Justificar"
      ok-variant="success"
      cancel-title="Cancelar"
      :ok-disabled="!justificarForm.categoria_convenio_id || (categoriaSeleccionadaResumen && categoriaSeleccionadaResumen.restantes !== null && categoriaSeleccionadaResumen.restantes <= 0)"
    >
      <div v-if="justificarForm.falta">
        <b-row class="mb-1">
          <b-col md="6">
            <strong>Usuario:</strong> {{ justificarForm.falta.usuario_nombre }}
          </b-col>
          <b-col md="6">
            <strong>Fecha:</strong> {{ formatFecha(justificarForm.falta.fecha) }}
          </b-col>
        </b-row>
        <b-row class="mb-1">
          <b-col md="5">
            <strong>Tipo:</strong>
            <b-badge :variant="justificarForm.falta.tipo === 'dia_completo' ? 'danger' : 'warning'" class="ml-1">
              {{ justificarForm.falta.tipo === 'dia_completo' ? 'Dia completo' : 'Parcial' }}
            </b-badge>
          </b-col>
        </b-row>
        <b-row class="mb-1">
          <b-col md="4">
            <strong>Esperadas:</strong> {{ formatDecimalAHoras(justificarForm.falta.horas_esperadas) }}
          </b-col>
          <b-col md="4">
            <strong>Trabajadas:</strong> {{ formatDecimalAHoras(justificarForm.falta.horas_trabajadas) }}
          </b-col>
        </b-row>

        <hr>

        <b-form-group label="Categoria del convenio *">
          <v-select
            v-model="justificarForm.categoria_convenio_id"
            :options="justificarForm.categorias"
            label="nombre"
            :reduce="c => c.id"
            placeholder="Seleccionar categoria"
          />
        </b-form-group>

        <b-alert
          v-if="categoriaSeleccionadaResumen"
          show
          :variant="categoriaSeleccionadaResumen.restantes !== null && categoriaSeleccionadaResumen.restantes <= 0 ? 'danger' : 'info'"
        >
          <small>
            <strong>{{ categoriaSeleccionadaResumen.nombre }}:</strong>
            <span v-if="categoriaSeleccionadaResumen.restantes !== null">
              {{ categoriaSeleccionadaResumen.usado }} {{ categoriaSeleccionadaResumen.tipo === 'horas' ? 'horas' : 'dias' }} usados de {{ categoriaSeleccionadaResumen.cantidad }}
              ({{ categoriaSeleccionadaResumen.restantes }} restantes)
            </span>
            <span v-else>
              {{ categoriaSeleccionadaResumen.usado }} {{ categoriaSeleccionadaResumen.tipo === 'horas' ? 'horas' : 'dias' }} usados (sin limite)
            </span>
          </small>
        </b-alert>

        <b-form-group label="Notas">
          <b-form-textarea
            v-model="justificarForm.notas"
            placeholder="Notas adicionales (opcional)..."
            rows="9"
          />
        </b-form-group>
      </div>
    </b-modal>

    <!-- Modal: Detalle faltas de un usuario -->
    <b-modal
      v-model="mostrarDetalleFaltas"
      :title="detalleUsuario ? ('Faltas de ' + detalleUsuario.usuario_nombre + ' — ' + resumenAnio) : 'Detalle de faltas'"
      size="xl"
      ok-only
      ok-title="Cerrar"
      ok-variant="secondary"
    >
      <b-table
        :items="faltasDetalleUsuario"
        :fields="columnasDetalleFaltas"
        responsive
        striped
        hover
        small
        show-empty
        empty-text="Sin faltas registradas"
      >
        <template #cell(fecha)="data">
          {{ formatFecha(data.item.fecha) }}
        </template>
        <template #cell(tipo)="data">
          <b-badge :variant="data.item.tipo === 'dia_completo' ? 'light-danger' : 'light-warning'">
            {{ data.item.tipo === 'dia_completo' ? 'Dia completo' : 'Parcial' }}
          </b-badge>
        </template>
        <template #cell(estado)="data">
          <b-badge
            :variant="data.item.estado === 'pendiente' ? 'warning' : data.item.estado === 'justificada' ? 'success' : data.item.estado === 'injustificada' ? 'danger' : 'secondary'"
          >
            {{ data.item.estado }}
          </b-badge>
        </template>
        <template #cell(horas_ausencia)="data">
          {{ data.item.horas_trabajadas || 0 }}h / {{ data.item.horas_esperadas || 0 }}h
        </template>
        <template #cell(categoria_nombre)="data">
          {{ data.item.categoria_nombre || '-' }}
        </template>
        <template #cell(notas)="data">
          {{ data.item.notas || '-' }}
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

export default {
  name: 'FichajeAdmin',
  components: {
    vSelect,
    flatPickr,
  },
  data() {
    return {
      // === Fichajes (original) ===
      usuarios: [],
      fichajes: [],
      filtroUsuario: null,
      filtroDesde: '',
      filtroHasta: '',
      sortBy: 'fecha',
      sortDesc: true,
      columnas: [
        { key: 'usuario', label: 'Usuario', sortable: true },
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'primera_entrada', label: 'Primera Entrada', sortable: true },
        { key: 'ultima_salida', label: 'Ultima Salida', sortable: true },
        { key: 'fichajes_dia', label: 'Fichajes' },
        { key: 'total', label: 'Total Horas' },
        { key: 'horas_extras', label: 'Horas Extras' },
        { key: 'descanso', label: 'Descanso' },
        { key: 'editado', label: '' },
        { key: 'acciones', label: '' },
      ],
      columnasResumen: [
        { key: 'nombre', label: 'Usuario' },
        { key: 'total_fichajes', label: 'Fichajes' },
        { key: 'total_horas', label: 'Horas Trabajadas' },
        { key: 'horas_extras', label: 'Horas Extras' },
        { key: 'acciones', label: '' },
      ],
      columnasHorasExtras: [
        { key: 'fecha', label: 'Fecha' },
        { key: 'horas_trabajadas', label: 'Horas Trabajadas' },
        { key: 'horas_extras', label: 'Horas Extras' },
      ],
      detalleHorasExtras: null,
      columnasDetalle: [
        { key: 'hora_entrada', label: 'Entrada' },
        { key: 'hora_salida', label: 'Salida' },
        { key: 'total', label: 'Duracion' },
        { key: 'modificado', label: '' },
        { key: 'acciones', label: '' },
      ],
      editForm: {
        id: null,
        usuario_nombre: '',
        fecha: null,
        hora_entrada: '',
        hora_salida: '',
        motivo: ''
      },
      fichajeCerrar: null,
      fichajeEliminar: null,
      nuevoForm: {
        usuario_id: null,
        fecha: '',
        hora_entrada: '',
        hora_salida: '',
        motivo: ''
      },
      detalleDia: null,
      pdfForm: {
        mes: '',
        usuario_id: null,
        empresa: 'CRAFT LAB SL UNIPERSONAL'
      },

      // === Faltas ===
      anioActual: new Date().getFullYear(),
      resumenAnio: new Date().getFullYear(),
      resumenFaltasAnual: [],
      resumenLoading: false,
      columnasResumenFaltas: [
        { key: 'usuario_nombre', label: 'Usuario', sortable: true },
        { key: 'total', label: 'Total', sortable: true },
        { key: 'dias_completos', label: 'Dia completo', sortable: true },
        { key: 'parciales', label: 'Parcial', sortable: true },
        { key: 'pendientes', label: 'Pendientes', sortable: true },
        { key: 'justificadas', label: 'Justificadas', sortable: true },
        { key: 'injustificadas', label: 'Injustificadas', sortable: true },
        { key: 'omitidas', label: 'Omitidas', sortable: true },
        { key: 'acciones', label: '' },
      ],
      faltasFiltroUsuario: null,
      faltasFiltroDesde: '',
      faltasFiltroHasta: '',
      faltasFiltroEstado: 'pendiente',
      opcionesEstadoFalta: [
        { value: '', label: 'Todas' },
        { value: 'pendiente', label: 'Pendientes' },
        { value: 'justificada', label: 'Justificadas' },
        { value: 'injustificada', label: 'Injustificadas' },
        { value: 'omitida', label: 'Omitidas' },
      ],
      faltasDetectadas: [],
      faltasLoading: false,
      columnasFaltas: [
        { key: 'usuario_nombre', label: 'Usuario', sortable: true },
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'tipo', label: 'Tipo' },
        { key: 'horas', label: 'Horas (trab/esper)' },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'acciones', label: '' },
      ],
      justificarForm: {
        falta: null,
        categoria_convenio_id: null,
        categorias: [],
        notas: ''
      },
      detalleUsuario: null,
      mostrarDetalleFaltas: false,
      columnasDetalleFaltas: [
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'tipo', label: 'Tipo', sortable: true },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'horas_ausencia', label: 'Horas (trab/esper)' },
        { key: 'categoria_nombre', label: 'Categoria' },
        { key: 'notas', label: 'Notas' },
      ],

      // === Horarios ===
      horarioUsuarioId: null,
      horarioEditable: [],
      horarioSaving: false,
      columnasHorario: [
        { key: 'dia', label: 'Dia' },
        { key: 'hora_entrada', label: 'Hora Entrada' },
        { key: 'hora_salida', label: 'Hora Salida' },
        { key: 'minutos_descanso', label: 'Descanso (min)' },
        { key: 'activo', label: 'Activo' },
      ],
    }
  },
  computed: {
    totalHorasPeriodo() {
      let totalMinutos = 0
      this.fichajes.forEach(item => {
        if (item.hora_entrada && item.hora_salida) {
          const entrada = new Date(item.hora_entrada)
          const salida = new Date(item.hora_salida)
          const diff = (salida - entrada) / 1000 / 60
          totalMinutos += diff
        }
      })
      const horas = Math.floor(totalMinutos / 60)
      const minutos = Math.round(totalMinutos % 60)
      return horas + 'h ' + minutos + 'm'
    },

    fichajesAbiertos() {
      return this.fichajes.filter(f => !f.hora_salida).length
    },

    resumenPorUsuario() {
      const resumen = {}
      const LIMITE_JORNADA = 450
      const fichajesPorUsuarioDia = {}

      this.fichajes.forEach(item => {
        const usuarioId = item.usuario_id
        const fecha = new Date(item.hora_entrada).toISOString().split('T')[0]
        const key = usuarioId + '_' + fecha

        if (!fichajesPorUsuarioDia[key]) {
          fichajesPorUsuarioDia[key] = {
            usuario_id: usuarioId,
            usuario_nombre: item.usuario_nombre,
            fecha: fecha,
            minutos: 0
          }
        }

        if (item.hora_entrada && item.hora_salida) {
          const entrada = new Date(item.hora_entrada)
          const salida = new Date(item.hora_salida)
          const diff = (salida - entrada) / 1000 / 60
          fichajesPorUsuarioDia[key].minutos += diff
        }
      })

      this.fichajes.forEach(item => {
        const usuarioId = item.usuario_id
        if (!resumen[usuarioId]) {
          resumen[usuarioId] = {
            usuario_id: usuarioId,
            nombre: item.usuario_nombre,
            total_fichajes: 0,
            minutos: 0,
            minutos_extras: 0,
            dias_extras: []
          }
        }
        resumen[usuarioId].total_fichajes++
        if (item.hora_entrada && item.hora_salida) {
          const entrada = new Date(item.hora_entrada)
          const salida = new Date(item.hora_salida)
          const diff = (salida - entrada) / 1000 / 60
          resumen[usuarioId].minutos += diff
        }
      })

      const MARGEN_MINUTOS = 6

      Object.values(fichajesPorUsuarioDia).forEach(dia => {
        if (dia.minutos > LIMITE_JORNADA) {
          const minutosExtras = dia.minutos - LIMITE_JORNADA
          if (resumen[dia.usuario_id]) {
            resumen[dia.usuario_id].minutos_extras += minutosExtras
            if (minutosExtras > MARGEN_MINUTOS) {
              resumen[dia.usuario_id].dias_extras.push({
                fecha: dia.fecha,
                minutos_trabajados: dia.minutos,
                minutos_extras: minutosExtras
              })
            }
          }
        }
      })

      return Object.values(resumen).map(function(r) {
        return Object.assign({}, r, {
          total_horas: Math.floor(r.minutos / 60) + 'h ' + Math.round(r.minutos % 60) + 'm',
          horas_extras: r.minutos_extras > 0
            ? Math.floor(r.minutos_extras / 60) + 'h ' + Math.round(r.minutos_extras % 60) + 'm'
            : '-'
        })
      })
    },

    hoyStr() {
      return new Date().toISOString().split('T')[0]
    },

    fichajosHoy() {
      return this.fichajosAgrupados.filter(g => g.fecha === this.hoyStr)
    },

    fichajosHistorial() {
      return this.fichajosAgrupados.filter(g => g.fecha !== this.hoyStr)
    },

    fichajosAgrupados() {
      const agrupados = {}

      this.fichajes.forEach(item => {
        const fecha = new Date(item.hora_entrada).toISOString().split('T')[0]
        const key = item.usuario_id + '_' + fecha

        if (!agrupados[key]) {
          agrupados[key] = {
            usuario_id: item.usuario_id,
            usuario_nombre: item.usuario_nombre,
            fecha: fecha,
            primera_entrada: item.hora_entrada,
            tipo_primera_entrada: item.tipo_entrada,
            ultima_salida: item.hora_salida,
            tipo_ultima_salida: item.tipo_salida,
            fichajes: [item],
            tiene_abierto: !item.hora_salida,
            tiene_editado: !!(item.fecha_modificacion || item.modificado_por)
          }
        } else {
          agrupados[key].fichajes.push(item)
          if (new Date(item.hora_entrada) < new Date(agrupados[key].primera_entrada)) {
            agrupados[key].primera_entrada = item.hora_entrada
            agrupados[key].tipo_primera_entrada = item.tipo_entrada
          }
          if (item.hora_salida) {
            if (!agrupados[key].ultima_salida || new Date(item.hora_salida) > new Date(agrupados[key].ultima_salida)) {
              agrupados[key].ultima_salida = item.hora_salida
              agrupados[key].tipo_ultima_salida = item.tipo_salida
            }
          }
          if (!item.hora_salida) {
            agrupados[key].tiene_abierto = true
          }
          if (item.fecha_modificacion || item.modificado_por) {
            agrupados[key].tiene_editado = true
          }
        }
      })

      return Object.values(agrupados).sort((a, b) => {
        if (a.fecha !== b.fecha) return new Date(b.fecha) - new Date(a.fecha)
        return a.usuario_nombre.localeCompare(b.usuario_nombre)
      })
    },

    usuariosConLlave() {
      return this.usuarios.filter(function(u) { return u.llave && u.llave.trim() !== '' })
    },

    usuariosConSS() {
      return this.usuarios.filter(function(u) { return u.numeroSS && u.numeroSS.trim() !== '' })
    },

    // === Faltas computed ===
    faltasPendientes() {
      return this.faltasDetectadas.filter(f => f.estado === 'pendiente')
    },
    faltasJustificadas() {
      return this.faltasDetectadas.filter(f => f.estado === 'justificada')
    },
    faltasInjustificadas() {
      return this.faltasDetectadas.filter(f => f.estado === 'injustificada')
    },
    faltasOmitidas() {
      return this.faltasDetectadas.filter(f => f.estado === 'omitida')
    },
    faltasFiltradas() {
      if (!this.faltasFiltroEstado) return this.faltasDetectadas
      return this.faltasDetectadas.filter(f => f.estado === this.faltasFiltroEstado)
    },
    resumenFaltasAgrupado() {
      var resumen = {}
      this.resumenFaltasAnual.forEach(function(f) {
        if (!resumen[f.usuario_id]) {
          resumen[f.usuario_id] = {
            usuario_id: f.usuario_id,
            usuario_nombre: f.usuario_nombre,
            total: 0,
            dias_completos: 0,
            parciales: 0,
            pendientes: 0,
            justificadas: 0,
            injustificadas: 0,
            omitidas: 0
          }
        }
        var r = resumen[f.usuario_id]
        r.total++
        if (f.tipo === 'dia_completo') r.dias_completos++
        else r.parciales++
        if (f.estado === 'pendiente') r.pendientes++
        else if (f.estado === 'justificada') r.justificadas++
        else if (f.estado === 'injustificada') r.injustificadas++
        else if (f.estado === 'omitida') r.omitidas++
      })
      return Object.values(resumen).sort(function(a, b) {
        return b.total - a.total
      })
    },
    faltasDetalleUsuario() {
      if (!this.detalleUsuario) return []
      var uid = this.detalleUsuario.usuario_id
      return this.resumenFaltasAnual
        .filter(function(f) { return f.usuario_id === uid })
        .sort(function(a, b) { return new Date(b.fecha) - new Date(a.fecha) })
    },
    categoriaSeleccionadaResumen() {
      if (!this.justificarForm.categoria_convenio_id) return null
      var cats = this.justificarForm.categorias
      for (var i = 0; i < cats.length; i++) {
        if (cats[i].id === this.justificarForm.categoria_convenio_id) {
          return cats[i]
        }
      }
      return null
    },
  },
  methods: {
    // === Fichajes methods (original) ===
    iconoTipo(tipo) {
      const iconos = {
        app: 'fa-mobile-screen',
        llave: 'fa-key',
        pin: 'fa-hashtag',
        admin: 'fa-user-shield',
      }
      return iconos[tipo] || null
    },

    colorTipo(tipo) {
      const colores = {
        app: '#28c76f',
        llave: '#ff9f43',
        pin: '#00cfe8',
        admin: '#7367f0',
      }
      return colores[tipo] || null
    },

    async cargarUsuarios() {
      try {
        const response = await axios.get('/usuarios')
        this.usuarios = response.data
      } catch (error) {
        console.error('Error al cargar usuarios:', error)
      }
    },

    abrirCrearFichaje() {
      this.nuevoForm = {
        usuario_id: null,
        fecha: new Date().toISOString().split('T')[0],
        hora_entrada: '',
        hora_salida: '',
        motivo: ''
      }
      this.$bvModal.show('modal-crear-fichaje')
    },

    abrirCrearFichajeDesdeDetalle() {
      if (!this.detalleDia) return
      this.nuevoForm = {
        usuario_id: this.detalleDia.usuario_id,
        fecha: this.detalleDia.fecha,
        hora_entrada: '',
        hora_salida: '',
        motivo: ''
      }
      this.$bvModal.show('modal-crear-fichaje')
    },

    async guardarNuevoFichaje(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (!this.nuevoForm.usuario_id || !this.nuevoForm.fecha || !this.nuevoForm.hora_entrada) {
        this.$bvToast.toast('Completa todos los campos obligatorios', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      try {
        const fecha = this.nuevoForm.fecha

        await axios.post('/fichajes/crear', {
          usuario_id: this.nuevoForm.usuario_id,
          hora_entrada: fecha + 'T' + this.nuevoForm.hora_entrada + ':00',
          hora_salida: this.nuevoForm.hora_salida ? fecha + 'T' + this.nuevoForm.hora_salida + ':00' : null,
          motivo: this.nuevoForm.motivo
        })

        this.$bvToast.toast('Fichaje creado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })

        this.$bvModal.hide('modal-crear-fichaje')
        await this.cargarFichajes()
        this.actualizarDetalleDia()
      } catch (error) {
        console.error('Error al crear fichaje:', error)
        var msg = (error.response && error.response.data && error.response.data.message) || 'Error al crear fichaje'
        this.$bvToast.toast(msg, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async cargarFichajes() {
      try {
        const params = {}
        if (this.filtroUsuario) params.usuario_id = this.filtroUsuario
        if (this.filtroDesde) params.desde = this.filtroDesde
        if (this.filtroHasta) params.hasta = this.filtroHasta

        const response = await axios.get('/fichajes/admin', { params })
        this.fichajes = response.data
      } catch (error) {
        console.error('Error al cargar fichajes:', error)
      }
    },

    cerrarFichaje(fichaje) {
      this.fichajeCerrar = fichaje
      this.$bvModal.show('modal-cerrar-fichaje')
    },

    async confirmarCerrarFichaje() {
      try {
        await axios.post('/fichajes/cerrar/' + this.fichajeCerrar.id)
        this.$bvToast.toast('Fichaje cerrado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })
        this.fichajeCerrar = null
        await this.cargarFichajes()
        this.actualizarDetalleDia()
      } catch (error) {
        console.error('Error al cerrar fichaje:', error)
        this.$bvToast.toast('Error al cerrar fichaje', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    abrirEliminarFichaje(fichaje) {
      this.fichajeEliminar = fichaje
      this.$bvModal.show('modal-eliminar-fichaje')
    },

    async confirmarEliminarFichaje() {
      try {
        await axios.delete('/fichajes/' + this.fichajeEliminar.id)
        this.$bvToast.toast('Fichaje eliminado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })
        this.fichajeEliminar = null
        await this.cargarFichajes()
        this.actualizarDetalleDia()
      } catch (error) {
        console.error('Error al eliminar fichaje:', error)
        var msg = (error.response && error.response.data && error.response.data.message) || 'Error al eliminar fichaje'
        this.$bvToast.toast(msg, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    limpiarFiltros() {
      this.filtroUsuario = null
      this.setFiltrosMesActual()
      this.cargarFichajes()
    },

    formatFecha(fecha) {
      if (!fecha) return ''
      return new Date(fecha).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },

    formatHora(datetime) {
      if (!datetime) return ''
      return new Date(datetime).toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatFechaHora(datetime) {
      if (!datetime) return ''
      var d = new Date(datetime)
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    },

    calcularTotalHoras(item) {
      if (!item.hora_entrada || !item.hora_salida) return '-'
      const entrada = new Date(item.hora_entrada)
      const salida = new Date(item.hora_salida)
      const diff = (salida - entrada) / 1000 / 60
      const horas = Math.floor(diff / 60)
      const minutos = Math.round(diff % 60)
      return horas + 'h ' + minutos + 'm'
    },

    calcularTotalHorasAgrupado(grupo) {
      let totalMinutos = 0
      grupo.fichajes.forEach(item => {
        if (item.hora_entrada && item.hora_salida) {
          const entrada = new Date(item.hora_entrada)
          const salida = new Date(item.hora_salida)
          const diff = (salida - entrada) / 1000 / 60
          totalMinutos += diff
        }
      })
      if (totalMinutos === 0 && grupo.tiene_abierto) return '-'
      const horas = Math.floor(totalMinutos / 60)
      const minutos = Math.round(totalMinutos % 60)
      return horas + 'h ' + minutos + 'm'
    },

    calcularHorasExtrasAgrupado(grupo) {
      const LIMITE_JORNADA = 450
      let totalMinutos = 0
      grupo.fichajes.forEach(item => {
        if (item.hora_entrada && item.hora_salida) {
          const entrada = new Date(item.hora_entrada)
          const salida = new Date(item.hora_salida)
          const diff = (salida - entrada) / 1000 / 60
          totalMinutos += diff
        }
      })
      if (totalMinutos <= LIMITE_JORNADA) return null
      const minutosExtras = totalMinutos - LIMITE_JORNADA
      const horas = Math.floor(minutosExtras / 60)
      const minutos = Math.round(minutosExtras % 60)
      return horas + 'h ' + minutos + 'm'
    },

    esHoraExtraSignificativa(grupo) {
      const LIMITE_JORNADA = 450
      const MARGEN_MINUTOS = 6
      let totalMinutos = 0
      grupo.fichajes.forEach(item => {
        if (item.hora_entrada && item.hora_salida) {
          const entrada = new Date(item.hora_entrada)
          const salida = new Date(item.hora_salida)
          const diff = (salida - entrada) / 1000 / 60
          totalMinutos += diff
        }
      })
      const minutosExtras = totalMinutos - LIMITE_JORNADA
      return minutosExtras > MARGEN_MINUTOS
    },

    abrirDetalleDia(grupo) {
      grupo.fichajes.forEach(function(f) {
        f._showDetails = !!(f.fecha_modificacion || f.modificado_por)
      })
      this.detalleDia = grupo
      this.$bvModal.show('modal-detalle-dia')
    },

    actualizarDetalleDia() {
      if (!this.detalleDia) return
      const grupoActualizado = this.fichajosAgrupados.find(
        g => g.usuario_id === this.detalleDia.usuario_id && g.fecha === this.detalleDia.fecha
      )
      if (grupoActualizado) {
        this.detalleDia = grupoActualizado
      } else {
        this.$bvModal.hide('modal-detalle-dia')
        this.detalleDia = null
      }
    },

    calcularDescanso(grupo) {
      if (grupo.fichajes.length < 2) return '-'
      const fichajosOrdenados = [].concat(grupo.fichajes).sort((a, b) =>
        new Date(a.hora_entrada) - new Date(b.hora_entrada)
      )
      let totalDescanso = 0
      for (let i = 0; i < fichajosOrdenados.length - 1; i++) {
        const fichActual = fichajosOrdenados[i]
        const fichSiguiente = fichajosOrdenados[i + 1]
        if (fichActual.hora_salida) {
          const salida = new Date(fichActual.hora_salida)
          const siguienteEntrada = new Date(fichSiguiente.hora_entrada)
          const diff = (siguienteEntrada - salida) / 1000 / 60
          if (diff > 0) {
            totalDescanso += diff
          }
        }
      }
      if (totalDescanso === 0) return '-'
      const horas = Math.floor(totalDescanso / 60)
      const minutos = Math.round(totalDescanso % 60)
      if (horas > 0) {
        return horas + 'h ' + minutos + 'm'
      }
      return minutos + 'm'
    },

    descargarCSV() {
      var self = this
      const csvContent =
        "data:text/csv;charset=utf-8,\uFEFF" +
        "Usuario,Fecha,Entrada,Salida,Total Horas\n" +
        this.fichajes.map(function(item) {
          return item.usuario_nombre + ',' + self.formatFecha(item.fecha) + ',' + self.formatHora(item.hora_entrada) + ',' + (item.hora_salida ? self.formatHora(item.hora_salida) : 'Abierto') + ',' + self.calcularTotalHoras(item)
        }).join("\n")

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', 'fichajes_admin_' + new Date().toISOString().split('T')[0] + '.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
    },

    setFiltrosMesActual() {
      const hoy = new Date()
      const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
      this.filtroDesde = primerDia.toISOString().split('T')[0]
      this.filtroHasta = hoy.toISOString().split('T')[0]
    },

    abrirEditarFichaje(fichaje) {
      this.editForm.id = fichaje.id
      this.editForm.usuario_nombre = fichaje.usuario_nombre
      this.editForm.fecha = fichaje.hora_entrada ? new Date(fichaje.hora_entrada).toISOString().split('T')[0] : ''
      this.editForm.hora_entrada = fichaje.hora_entrada ? new Date(fichaje.hora_entrada).toTimeString().slice(0, 5) : ''
      this.editForm.hora_salida = fichaje.hora_salida ? new Date(fichaje.hora_salida).toTimeString().slice(0, 5) : ''
      this.editForm.motivo = ''
      this.$bvModal.show('modal-editar-fichaje-admin')
    },

    async guardarEdicion(bvModalEvent) {
      bvModalEvent.preventDefault()

      try {
        const fecha = this.editForm.fecha

        await axios.put('/fichajes/' + this.editForm.id, {
          hora_entrada: this.editForm.hora_entrada ? fecha + 'T' + this.editForm.hora_entrada + ':00' : null,
          hora_salida: this.editForm.hora_salida ? fecha + 'T' + this.editForm.hora_salida + ':00' : null,
          motivo: this.editForm.motivo
        })

        this.$bvToast.toast('Fichaje actualizado correctamente', {
          title: 'Fichaje',
          variant: 'success',
          solid: true,
        })

        this.$bvModal.hide('modal-editar-fichaje-admin')
        await this.cargarFichajes()
        this.actualizarDetalleDia()
      } catch (error) {
        console.error('Error al editar fichaje:', error)
        var msg = (error.response && error.response.data && error.response.data.message) || 'Error al editar fichaje'
        this.$bvToast.toast(msg, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    abrirModalPDF() {
      const hoy = new Date()
      this.pdfForm.mes = hoy.getFullYear() + '-' + String(hoy.getMonth() + 1).padStart(2, '0')
      this.$bvModal.show('modal-pdf')
    },

    async generarPDF(bvModalEvent) {
      bvModalEvent.preventDefault()

      try {
        const [year, month] = this.pdfForm.mes.split('-')
        const primerDia = year + '-' + month + '-01'
        const ultimoDia = new Date(year, month, 0).toISOString().split('T')[0]

        const params = {
          desde: primerDia,
          hasta: ultimoDia
        }

        if (this.pdfForm.usuario_id) {
          params.usuario_id = this.pdfForm.usuario_id
        }

        const response = await axios.get('/fichajes/admin', { params })
        const fichajes = response.data

        if (this.pdfForm.usuario_id) {
          const usuario = this.usuarios.find(u => u.id === this.pdfForm.usuario_id)
          this.generarPDFUsuario(fichajes, usuario, year, month)
        } else {
          this.generarPDFTodos(fichajes, year, month)
        }

        this.$bvModal.hide('modal-pdf')
        this.$bvToast.toast('PDF generado correctamente', {
          title: 'PDF',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Error al generar PDF:', error)
        this.$bvToast.toast('Error al generar PDF', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    generarPDFUsuario(fichajes, usuario, year, month, doc, addPage) {
      doc = doc || null
      addPage = addPage || false
      const isNewDoc = !doc
      if (isNewDoc) {
        doc = new jsPDF('portrait', 'mm', 'a4')
      } else if (addPage) {
        doc.addPage()
      }

      const diasEnMes = new Date(year, month, 0).getDate()
      const nombreMes = new Date(year, month - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })

      const fichajesPorDia = {}
      fichajes.filter(f => f.usuario_id === usuario.id).forEach(f => {
        const dia = new Date(f.hora_entrada).getDate()
        if (!fichajesPorDia[dia]) {
          fichajesPorDia[dia] = []
        }
        fichajesPorDia[dia].push(f)
      })

      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('REGISTRO DE JORNADA DE TRABAJADORES', 105, 12, { align: 'center' })

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.3)

      doc.rect(12, 15, 186, 16)

      doc.setFont('helvetica', 'bold')
      doc.text('Empresa:', 14, 21)
      doc.text('Trabajador:', 110, 21)
      doc.setFont('helvetica', 'normal')
      doc.text('' + this.pdfForm.empresa, 32, 21)
      doc.text('' + (usuario.nombreCompleto || usuario.nombre), 132, 21)

      doc.setFont('helvetica', 'bold')
      doc.text('N.I.F.:', 110, 27)
      doc.text('Afiliacion S.S.:', 145, 27)
      doc.setFont('helvetica', 'normal')
      doc.text('' + (usuario.nif || ''), 120, 27)
      doc.text('' + (usuario.numeroSS || ''), 168, 27)

      doc.rect(12, 32, 186, 7)

      doc.setFont('helvetica', 'bold')
      doc.text('Periodo:', 14, 37)
      doc.setFont('helvetica', 'normal')
      doc.text(nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1), 32, 37)

      const tableStartY = 42
      const tableData = []
      const diasSemana = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado']

      for (let dia = 1; dia <= diasEnMes; dia++) {
        const fecha = new Date(year, month - 1, dia)
        const diaSemana = diasSemana[fecha.getDay()]
        const esFinDeSemana = fecha.getDay() === 0 || fecha.getDay() === 6

        const fichajesDia = fichajesPorDia[dia] || []

        let primeraEntrada = ''
        let ultimaSalida = ''
        let numFichajes = ''
        let horasJornada = ''
        let horasDescanso = ''
        let horasExtras = ''
        let fueModificado = false

        if (fichajesDia.length > 0) {
          fueModificado = fichajesDia.some(f => f.fecha_modificacion || f.modificado_por)
          fichajesDia.sort((a, b) => new Date(a.hora_entrada) - new Date(b.hora_entrada))

          numFichajes = fichajesDia.length.toString()
          primeraEntrada = new Date(fichajesDia[0].hora_entrada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })

          const fichajesConSalida = fichajesDia.filter(f => f.hora_salida)
          if (fichajesConSalida.length > 0) {
            fichajesConSalida.sort((a, b) => new Date(b.hora_salida) - new Date(a.hora_salida))
            ultimaSalida = new Date(fichajesConSalida[0].hora_salida).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
          }

          let totalMinutosTrabajados = 0
          fichajesDia.forEach(f => {
            if (f.hora_entrada && f.hora_salida) {
              const entrada = new Date(f.hora_entrada)
              const salida = new Date(f.hora_salida)
              totalMinutosTrabajados += (salida - entrada) / 1000 / 60
            }
          })

          let totalMinutosDescanso = 0
          if (fichajesDia.length > 1) {
            for (let i = 0; i < fichajesDia.length - 1; i++) {
              const fichActual = fichajesDia[i]
              const fichSiguiente = fichajesDia[i + 1]
              if (fichActual.hora_salida) {
                const salida = new Date(fichActual.hora_salida)
                const siguienteEntrada = new Date(fichSiguiente.hora_entrada)
                const diff = (siguienteEntrada - salida) / 1000 / 60
                if (diff > 0) {
                  totalMinutosDescanso += diff
                }
              }
            }
          }

          if (totalMinutosTrabajados > 0) {
            if (totalMinutosTrabajados > 450) {
              horasJornada = '7:30'
              const minutosE2 = totalMinutosTrabajados - 450
              const horasE = Math.floor(minutosE2 / 60)
              const minutosE = Math.round(minutosE2 % 60)
              horasExtras = horasE + ':' + String(minutosE).padStart(2, '0')
            } else {
              const horas = Math.floor(totalMinutosTrabajados / 60)
              const minutos = Math.round(totalMinutosTrabajados % 60)
              horasJornada = horas + ':' + String(minutos).padStart(2, '0')
            }
          }

          if (totalMinutosDescanso > 0) {
            const horasD = Math.floor(totalMinutosDescanso / 60)
            const minutosD = Math.round(totalMinutosDescanso % 60)
            if (horasD > 0) {
              horasDescanso = horasD + ':' + String(minutosD).padStart(2, '0')
            } else {
              horasDescanso = minutosD + 'm'
            }
          }
        }

        const diaTexto = dia + ' ' + diaSemana + (fueModificado ? ' *' : '')
        tableData.push([
          { content: diaTexto, styles: { textColor: esFinDeSemana ? [180, 0, 0] : [0, 0, 0] } },
          primeraEntrada,
          ultimaSalida,
          numFichajes,
          horasJornada,
          horasDescanso,
          horasExtras
        ])
      }

      doc.autoTable({
        startY: tableStartY,
        head: [['Dia', 'Entrada', 'Salida', 'Fichajes', 'Horas jornada', 'Descanso', 'Horas extras']],
        body: tableData,
        styles: {
          fontSize: 9,
          cellPadding: 1.5,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          halign: 'center',
        },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: 26, halign: 'center' },
          2: { cellWidth: 26, halign: 'center' },
          3: { cellWidth: 18, halign: 'center' },
          4: { cellWidth: 26, halign: 'center' },
          5: { cellWidth: 26, halign: 'center' },
          6: { cellWidth: 26, halign: 'center' },
        },
        theme: 'grid',
        margin: { left: 14, right: 14 }
      })

      const finalY = doc.lastAutoTable.finalY + 3
      doc.setFontSize(7)
      doc.setFont('helvetica', 'italic')
      doc.text('* Registro modificado manualmente', 14, finalY)

      const firmaY = finalY + 4

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text('Firma Empresa:', 14, firmaY)
      doc.rect(14, firmaY + 2, 80, 25)

      doc.text('Firma Trabajador:', 110, firmaY)
      doc.rect(110, firmaY + 2, 80, 25)

      if (isNewDoc) {
        const pdfUrl = doc.output('bloburl')
        window.open(pdfUrl, '_blank')
      }

      return doc
    },

    rowClassResumen(item, type) {
      if (!item || type !== 'row') return ''
      if (item.dias_extras && item.dias_extras.length > 0) return 'row-horas-extras'
      return ''
    },

    abrirDetalleHorasExtras(usuario) {
      this.detalleHorasExtras = {
        nombre: usuario.nombre,
        dias: usuario.dias_extras.map(function(d) {
          return {
            fecha: d.fecha,
            horas_trabajadas: Math.floor(d.minutos_trabajados / 60) + 'h ' + Math.round(d.minutos_trabajados % 60) + 'm',
            horas_extras: Math.floor(d.minutos_extras / 60) + 'h ' + Math.round(d.minutos_extras % 60) + 'm'
          }
        }),
        total_extras: usuario.horas_extras
      }
      this.$bvModal.show('modal-horas-extras')
    },

    generarPDFTodos(fichajes, year, month) {
      let doc = null
      const usuariosConFichajes = []
      const idsVistos = {}
      fichajes.forEach(function(f) {
        if (!idsVistos[f.usuario_id]) {
          idsVistos[f.usuario_id] = true
          usuariosConFichajes.push(f.usuario_id)
        }
      })

      if (usuariosConFichajes.length === 0) {
        this.$bvToast.toast('No hay fichajes en el periodo seleccionado', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
        })
        return
      }

      var self = this
      var pageIndex = 0
      usuariosConFichajes.forEach(function(usuarioId) {
        const usuario = self.usuarios.find(u => u.id === usuarioId)
        if (usuario && usuario.numeroSS && usuario.numeroSS.trim() !== '') {
          doc = self.generarPDFUsuario(fichajes, usuario, year, month, doc, pageIndex > 0)
          pageIndex++
        }
      })

      if (doc) {
        const pdfUrl = doc.output('bloburl')
        window.open(pdfUrl, '_blank')
      }
    },

    // === Faltas methods ===
    async cargarResumenAnual() {
      try {
        this.resumenLoading = true
        var anio = this.resumenAnio
        var hoy = new Date()
        var inicioSistema = '2026-01-29'
        var desde = anio + '-01-01'
        if (desde < inicioSistema) desde = inicioSistema
        var hasta = anio === hoy.getFullYear()
          ? hoy.getFullYear() + '-' + String(hoy.getMonth() + 1).padStart(2, '0') + '-' + String(hoy.getDate()).padStart(2, '0')
          : anio + '-12-31'

        var response = await axios.get('/fichajes/faltas', { params: { desde: desde, hasta: hasta } })
        this.resumenFaltasAnual = response.data
      } catch (error) {
        console.error('Error al cargar resumen anual:', error)
      } finally {
        this.resumenLoading = false
      }
    },
    cambiarAnioResumen(anio) {
      this.resumenAnio = anio
      this.cargarResumenAnual()
    },
    abrirDetalleFaltas(item) {
      this.detalleUsuario = { usuario_id: item.usuario_id, usuario_nombre: item.usuario_nombre }
      this.mostrarDetalleFaltas = true
    },
    onTabFaltas() {
      var inicioSistema = '2026-01-29'
      if (!this.faltasFiltroDesde) {
        var hoy = new Date()
        var primerDia = new Date(hoy.getFullYear(), hoy.getMonth() - 1, hoy.getDate())
        this.faltasFiltroDesde = primerDia.toISOString().split('T')[0]
        if (this.faltasFiltroDesde < inicioSistema) this.faltasFiltroDesde = inicioSistema
        this.faltasFiltroHasta = hoy.toISOString().split('T')[0]
      }
      this.detectarFaltas()
      this.cargarResumenAnual()
    },

    async detectarFaltas() {
      try {
        this.faltasLoading = true
        var params = {
          desde: this.faltasFiltroDesde,
          hasta: this.faltasFiltroHasta
        }
        if (this.faltasFiltroUsuario) {
          params.usuario_id = this.faltasFiltroUsuario
        }

        var response = await axios.get('/fichajes/faltas', { params: params })
        this.faltasDetectadas = response.data
      } catch (error) {
        console.error('Error al detectar faltas:', error)
        this.$bvToast.toast('Error al detectar faltas', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.faltasLoading = false
      }
    },

    formatDecimalAHoras(decimal) {
      var h = Math.floor(decimal)
      var m = Math.round((decimal - h) * 60)
      return h + 'h ' + String(m).padStart(2, '0') + 'm'
    },

    variantEstadoFalta(estado) {
      var variants = {
        pendiente: 'warning',
        justificada: 'success',
        injustificada: 'danger',
        omitida: 'secondary'
      }
      return variants[estado] || 'secondary'
    },

    async abrirJustificarFalta(falta) {
      this.justificarForm.falta = falta
      this.justificarForm.categoria_convenio_id = falta.categoria_convenio_id || null
      this.justificarForm.notas = falta.notas || ''
      this.justificarForm.categorias = []

      // Cargar categorias del convenio del usuario
      try {
        var response = await axios.get('/fichajes/faltas/resumen/' + falta.usuario_id)
        var data = response.data
        if (data.categorias && data.categorias.length > 0) {
          this.justificarForm.categorias = data.categorias
        }
      } catch (error) {
        console.error('Error al cargar resumen convenio:', error)
      }

      this.$bvModal.show('modal-justificar-falta')
    },

    async confirmarJustificarFalta(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (!this.justificarForm.categoria_convenio_id) {
        this.$bvToast.toast('Selecciona una categoria del convenio', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      try {
        var falta = this.justificarForm.falta
        await axios.post('/fichajes/faltas/justificar', {
          usuario_id: falta.usuario_id,
          fecha: falta.fecha,
          tipo: falta.tipo,
          horas_esperadas: falta.horas_esperadas,
          horas_trabajadas: falta.horas_trabajadas,
          horas_ausencia: falta.horas_ausencia,
          categoria_convenio_id: this.justificarForm.categoria_convenio_id,
          notas: this.justificarForm.notas
        })

        this.$bvToast.toast('Falta justificada correctamente', {
          title: 'Faltas',
          variant: 'success',
          solid: true,
        })

        this.$bvModal.hide('modal-justificar-falta')
        this.detectarFaltas()
        this.cargarResumenAnual()
      } catch (error) {
        console.error('Error al justificar falta:', error)
        this.$bvToast.toast('Error al justificar falta', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async marcarInjustificada(falta) {
      try {
        await axios.post('/fichajes/faltas/injustificada', {
          usuario_id: falta.usuario_id,
          fecha: falta.fecha,
          tipo: falta.tipo,
          horas_esperadas: falta.horas_esperadas,
          horas_trabajadas: falta.horas_trabajadas,
          horas_ausencia: falta.horas_ausencia
        })

        this.$bvToast.toast('Falta marcada como injustificada', {
          title: 'Faltas',
          variant: 'warning',
          solid: true,
        })

        this.detectarFaltas()
        this.cargarResumenAnual()
      } catch (error) {
        console.error('Error al marcar falta como injustificada:', error)
        this.$bvToast.toast('Error al marcar falta', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async marcarOmitida(falta) {
      try {
        await axios.post('/fichajes/faltas/omitir', {
          usuario_id: falta.usuario_id,
          fecha: falta.fecha,
          tipo: falta.tipo,
          horas_esperadas: falta.horas_esperadas,
          horas_trabajadas: falta.horas_trabajadas,
          horas_ausencia: falta.horas_ausencia
        })

        this.$bvToast.toast('Falta marcada como omitida', {
          title: 'Faltas',
          variant: 'secondary',
          solid: true,
        })

        this.detectarFaltas()
        this.cargarResumenAnual()
      } catch (error) {
        console.error('Error al omitir falta:', error)
        this.$bvToast.toast('Error al omitir falta', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async marcarPendiente(falta) {
      try {
        await axios.post('/fichajes/faltas/pendiente', {
          usuario_id: falta.usuario_id,
          fecha: falta.fecha
        })

        this.$bvToast.toast('Falta devuelta a pendiente', {
          title: 'Faltas',
          variant: 'info',
          solid: true,
        })

        this.detectarFaltas()
        this.cargarResumenAnual()
      } catch (error) {
        console.error('Error al marcar falta como pendiente:', error)
        this.$bvToast.toast('Error al cambiar estado', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },

    // === Horarios methods ===
    onTabHorarios() {
      // noop, la carga se hace al seleccionar usuario
    },

    async cargarHorarioUsuario() {
      if (!this.horarioUsuarioId) {
        this.horarioEditable = []
        return
      }

      var diasNombres = ['', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo']
      var horariosDefault = []
      for (var i = 1; i <= 7; i++) {
        horariosDefault.push({
          dia_semana: i,
          dia_nombre: diasNombres[i],
          hora_entrada: i <= 5 ? '07:00' : '',
          hora_salida: i <= 5 ? '15:00' : '',
          minutos_descanso: i <= 5 ? 30 : 0,
          activo: i <= 5
        })
      }

      try {
        var response = await axios.get('/fichajes/horarios/' + this.horarioUsuarioId)
        var horariosBD = response.data

        if (horariosBD.length > 0) {
          horariosDefault.forEach(function(def) {
            var encontrado = null
            for (var j = 0; j < horariosBD.length; j++) {
              if (horariosBD[j].dia_semana === def.dia_semana) {
                encontrado = horariosBD[j]
                break
              }
            }
            if (encontrado) {
              def.hora_entrada = encontrado.hora_entrada ? encontrado.hora_entrada.substring(0, 5) : ''
              def.hora_salida = encontrado.hora_salida ? encontrado.hora_salida.substring(0, 5) : ''
              def.minutos_descanso = encontrado.minutos_descanso || 0
              def.activo = encontrado.activo === 1
            }
          })
        }

        this.horarioEditable = horariosDefault
      } catch (error) {
        console.error('Error al cargar horario:', error)
        this.horarioEditable = horariosDefault
      }
    },

    async guardarHorario() {
      if (!this.horarioUsuarioId) return

      try {
        this.horarioSaving = true

        await axios.post('/fichajes/horarios/' + this.horarioUsuarioId, {
          horarios: this.horarioEditable.map(function(h) {
            return {
              dia_semana: h.dia_semana,
              hora_entrada: h.hora_entrada,
              hora_salida: h.hora_salida,
              minutos_descanso: parseInt(h.minutos_descanso) || 0,
              activo: h.activo
            }
          })
        })

        this.$bvToast.toast('Horario guardado correctamente', {
          title: 'Horarios',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Error al guardar horario:', error)
        this.$bvToast.toast('Error al guardar horario', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.horarioSaving = false
      }
    },
  },
  mounted() {
    axios.post("/uso", { seccion: "fichaje-admin" })
    this.setFiltrosMesActual()
    this.cargarUsuarios()
    this.cargarFichajes()
    this.onTabFaltas()
  }
}
</script>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';
@import '../@core/scss/vue/libs/vue-flatpicker.scss';
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// ===== Design tokens (Data-Dense Dashboard - same as FabricandoV2) =====
$fa-primary: #3B82F6;
$fa-primary-soft: #60A5FA;
$fa-primary-glow: rgba(59, 130, 246, 0.18);
$fa-cta: #F97316;
$fa-bg: #F8FAFC;
$fa-surface: #FFFFFF;
$fa-surface-2: #F1F5F9;
$fa-border: #E2E8F0;
$fa-border-strong: #CBD5E1;
$fa-text: #1E293B;
$fa-text-mute: #64748B;
$fa-text-soft: #94A3B8;
$fa-danger: #EF4444;
$fa-info: #06B6D4;

.fichaje-admin-v2 {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: $fa-text;

  // ===== HEADER =====
  .fa-dash-header {
    background: $fa-surface;
    border: 1px solid $fa-border;
    border-radius: 14px;
    padding: 18px 20px 14px;
    margin-bottom: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .fa-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $fa-primary;
    letter-spacing: 0.14em;
    margin-bottom: 4px;

    &::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      background: $fa-cta;
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: middle;
      animation: fa-pulse 2s infinite;
    }
  }

  .fa-title {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    color: $fa-text;
    letter-spacing: -0.01em;
  }

  // ===== KPI STRIP =====
  .fa-kpi-strip {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    &.fa-kpi-strip-5 { grid-template-columns: repeat(5, 1fr); }

    @media (max-width: 1100px) {
      grid-template-columns: repeat(2, 1fr);
      &.fa-kpi-strip-5 { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      &.fa-kpi-strip-5 { grid-template-columns: 1fr; }
    }
  }

  .fa-kpi-card {
    background: $fa-bg;
    border: 1px solid $fa-border;
    border-radius: 10px;
    padding: 12px 14px;
    transition: all 200ms ease;

    &:hover {
      border-color: $fa-primary-soft;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
    }
  }

  .fa-kpi-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9.5px;
    font-weight: 600;
    color: $fa-text-soft;
    letter-spacing: 0.12em;
    margin-bottom: 6px;
  }

  .fa-kpi-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 26px;
    font-weight: 700;
    color: $fa-text;
    line-height: 1.1;
    margin-bottom: 6px;
    font-feature-settings: "tnum", "zero";
  }

  .fa-kpi-foot {
    font-size: 10.5px;
    color: $fa-text-mute;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .fa-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;

    &.pulse { animation: fa-pulse 2s infinite; }
  }
  .fa-dot-blue { background: $fa-primary; }
  .fa-dot-cyan { background: $fa-info; }
  .fa-dot-orange { background: $fa-cta; }
  .fa-dot-green { background: #10B981; }
  .fa-dot-red { background: $fa-danger; }
  .fa-dot-grey { background: $fa-text-soft; }

  // ===== B-CARD WRAPPER =====
  > .card {
    background: $fa-surface !important;
    border: 1px solid $fa-border !important;
    border-radius: 14px !important;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
    overflow: hidden;
    color: $fa-text !important;
  }

  .card-body { background: $fa-surface !important; color: $fa-text !important; }

  // ===== B-TABS (clean underline) =====
  .nav-tabs {
    background: $fa-surface !important;
    border-bottom: 1px solid $fa-border !important;
    padding: 0 20px;
    gap: 4px;
    margin: 0 !important;

    .nav-item { margin: 0 !important; }

    .nav-link {
      font-family: 'Plus Jakarta Sans', sans-serif !important;
      font-size: 12px !important;
      font-weight: 600 !important;
      color: $fa-text-mute !important;
      letter-spacing: 0.02em !important;
      border: 0 !important;
      border-radius: 0 !important;
      padding: 16px 18px !important;
      margin: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      transition: color 200ms ease !important;
      display: inline-flex !important;
      align-items: center !important;
      gap: 8px;
      position: relative;

      // Kill all Vuexy/bootstrap pseudo-elements
      &::before, &::after { display: none !important; }

      // Custom underline using box-shadow inset for crisp render
      &::after {
        content: '' !important;
        display: block !important;
        position: absolute !important;
        left: 14px;
        right: 14px;
        bottom: -1px;
        height: 2px;
        background: transparent;
        border-radius: 2px 2px 0 0;
        transition: background 200ms ease;
      }

      &:hover {
        color: $fa-text !important;
        background: transparent !important;

        &::after { background: rgba(59, 130, 246, 0.25); }
      }

      &.active {
        color: $fa-primary !important;
        background: transparent !important;
        font-weight: 700 !important;

        &::after { background: $fa-primary !important; }
      }

      .badge {
        font-family: 'JetBrains Mono', monospace !important;
        font-size: 10px !important;
        font-weight: 700 !important;
        padding: 2px 6px !important;
        border-radius: 10px !important;
        line-height: 1.2;
      }
    }
  }

  // ===== BUTTONS =====
  .btn {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    padding: 8px 14px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    letter-spacing: 0;
    transition: all 200ms ease;
    border-width: 1px;
    box-shadow: none !important;

    i, svg { font-size: 12px; }

    &.btn-primary,
    &.btn-flat-primary {
      background: $fa-primary !important;
      border-color: $fa-primary !important;
      color: #fff !important;

      &:hover:not(:disabled) {
        background: darken($fa-primary, 6%) !important;
        border-color: darken($fa-primary, 6%) !important;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25) !important;
      }
    }

    &.btn-outline-primary {
      background: transparent !important;
      border-color: $fa-border !important;
      color: $fa-primary !important;

      &:hover:not(:disabled) {
        background: rgba(59, 130, 246, 0.06) !important;
        border-color: $fa-primary !important;
      }
    }

    &.btn-outline-danger {
      background: transparent !important;
      border-color: $fa-border !important;
      color: $fa-danger !important;

      &:hover:not(:disabled) {
        background: rgba(239, 68, 68, 0.06) !important;
        border-color: $fa-danger !important;
      }
    }

    &.btn-outline-success,
    &.btn-flat-success {
      background: transparent !important;
      border-color: $fa-border !important;
      color: #10B981 !important;

      &:hover:not(:disabled) {
        background: rgba(16, 185, 129, 0.06) !important;
        border-color: #10B981 !important;
      }
    }

    &.btn-outline-warning,
    &.btn-flat-warning {
      background: transparent !important;
      border-color: $fa-border !important;
      color: $fa-cta !important;

      &:hover:not(:disabled) {
        background: rgba(249, 115, 22, 0.06) !important;
        border-color: $fa-cta !important;
      }
    }

    &.btn-outline-info,
    &.btn-flat-info {
      background: transparent !important;
      border-color: $fa-border !important;
      color: $fa-info !important;

      &:hover:not(:disabled) {
        background: rgba(6, 182, 212, 0.06) !important;
        border-color: $fa-info !important;
      }
    }

    &.btn-outline-secondary,
    &.btn-flat-secondary {
      background: transparent !important;
      border-color: $fa-border !important;
      color: $fa-text-mute !important;

      &:hover:not(:disabled) {
        background: rgba(15, 23, 42, 0.04) !important;
        border-color: $fa-text-mute !important;
        color: $fa-text !important;
      }
    }

    &.btn-sm {
      height: 30px;
      padding: 6px 10px;
      font-size: 11px;
    }
  }

  .btn-group .btn { border-radius: 0; }
  .btn-group .btn:first-child { border-top-left-radius: 8px; border-bottom-left-radius: 8px; }
  .btn-group .btn:last-child { border-top-right-radius: 8px; border-bottom-right-radius: 8px; }

  // ===== B-TABLE =====
  .table {
    margin-bottom: 0;
    background: $fa-surface !important;
    color: $fa-text !important;

    thead th {
      font-family: 'JetBrains Mono', monospace !important;
      font-size: 10px !important;
      font-weight: 600 !important;
      color: $fa-text-soft !important;
      letter-spacing: 0.08em !important;
      text-transform: uppercase;
      background: $fa-surface !important;
      border-bottom: 1px solid $fa-border !important;
      border-top: 0 !important;
      padding: 13px 14px !important;
    }

    tbody tr {
      transition: background 150ms ease;
      background: $fa-surface !important;

      td {
        border-top: 1px solid $fa-border !important;
        background: transparent !important;
        font-size: 13px;
        color: $fa-text !important;
        padding: 11px 14px !important;
        vertical-align: middle;
      }

      &:hover {
        background: rgba(59, 130, 246, 0.04) !important;
        td { background: transparent !important; }
      }
      &.row-horas-extras {
        background: rgba(239, 68, 68, 0.06) !important;
        &:hover { background: rgba(239, 68, 68, 0.1) !important; }
      }
    }

    strong { color: $fa-text !important; }
    .text-muted { color: $fa-text-soft !important; }
  }

  .tabla-hoy {
    border-left: 3px solid #10B981 !important;
  }

  // ===== FORM CONTROLS =====
  .form-group label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $fa-text-soft;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .form-control {
    border-radius: 8px;
    background: $fa-surface !important;
    border: 1px solid $fa-border !important;
    color: $fa-text !important;
    font-size: 12px;
    height: 36px;
    transition: all 200ms ease;

    &::placeholder { color: $fa-text-soft; }

    &:focus {
      border-color: $fa-primary !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12) !important;
    }
  }

  // ===== FLATPICKR DATE INPUT =====
  .date-input,
  input.date-input,
  .date-input.flatpickr-input,
  input.date-input.flatpickr-input,
  .date-input.form-control {
    display: block;
    width: 100% !important;
    height: 36px !important;
    padding: 0 12px !important;
    background: $fa-surface !important;
    background-color: $fa-surface !important;
    border: 1px solid $fa-border !important;
    border-radius: 8px !important;
    font-size: 12px !important;
    font-family: 'JetBrains Mono', monospace !important;
    color: $fa-text !important;
    line-height: 34px !important;
    transition: all 200ms ease;
    box-shadow: none !important;

    &::placeholder { color: $fa-text-soft; }

    &:focus, &:focus-visible, &.active {
      outline: none !important;
      border-color: $fa-primary !important;
      background: $fa-surface !important;
      background-color: $fa-surface !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12) !important;
    }
  }

  // v-select
  .v-select {
    .vs__dropdown-toggle {
      background: $fa-surface !important;
      border: 1px solid $fa-border !important;
      border-radius: 8px !important;
      height: 36px;
    }
    .vs__selected, .vs__search { color: $fa-text !important; font-size: 12px; }
    .vs__open-indicator { fill: $fa-text-mute !important; }
    .vs__dropdown-menu {
      background: $fa-surface !important;
      border: 1px solid $fa-border !important;
      color: $fa-text !important;
    }
    .vs__dropdown-option--highlight { background: rgba(59, 130, 246, 0.08) !important; color: $fa-primary !important; }
  }

  // Section subtitles ("Hoy", "Historial", "Resumen por usuario")
  h5 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: $fa-text;
    letter-spacing: -0.01em;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i { color: $fa-primary; }
  }

  // Resumen anual
  .resumen-anual-section {
    border: 1px solid $fa-border !important;
    background: $fa-bg !important;
    border-radius: 12px;
    border-top: 1px solid $fa-border !important;
  }
  .resumen-anual-icon { color: $fa-primary !important; }
  .resumen-anual-header small { color: $fa-text-mute !important; }

  // Badges (light-* variants from bootstrap-vue)
  .badge {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    letter-spacing: 0.02em;
    border-radius: 6px;
    padding: 4px 8px;

    &.badge-light-primary { background: rgba(59, 130, 246, 0.12) !important; color: $fa-primary !important; }
    &.badge-light-success { background: rgba(16, 185, 129, 0.12) !important; color: #10B981 !important; }
    &.badge-light-warning { background: rgba(249, 115, 22, 0.12) !important; color: $fa-cta !important; }
    &.badge-light-danger { background: rgba(239, 68, 68, 0.12) !important; color: $fa-danger !important; }
    &.badge-light-info { background: rgba(6, 182, 212, 0.12) !important; color: $fa-info !important; }
    &.badge-light-secondary { background: rgba(148, 163, 184, 0.12) !important; color: $fa-text-mute !important; }

    &.badge-warning { background: $fa-cta !important; color: #fff !important; }
    &.badge-danger { background: $fa-danger !important; color: #fff !important; }
    &.badge-success { background: #10B981 !important; color: #fff !important; }
    &.badge-secondary { background: $fa-text-soft !important; color: #fff !important; }
  }

  // Tab content background
  .tab-content { background: $fa-surface !important; padding: 20px !important; }

  // Generic text
  .text-muted, small { color: $fa-text-mute !important; }
  strong { color: $fa-text; }

  @keyframes fa-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.55; transform: scale(0.85); }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }
}

// ===== DARK MODE ===== (paleta negra, misma que pedidos Vero)
// Las reglas claras usan !important, así que aquí también
.dark-layout .fichaje-admin-v2 {
  color: #d0d2d6;

  .fa-dash-header { background: #1f1f1f; border-color: #2c2c2c; box-shadow: none; }
  > .card { background: #1f1f1f !important; border-color: #2c2c2c !important; box-shadow: none !important; color: #d0d2d6 !important; }
  .card-header { background: #1f1f1f !important; border-color: #2c2c2c !important; }
  .card-body,
  .tab-content { background: #1f1f1f !important; color: #d0d2d6 !important; }
  .fa-title { color: #e4e6eb; }

  .fa-kpi-card {
    background: #161616;
    border-color: #2c2c2c;
    &:hover { border-color: $fa-primary; box-shadow: none; }
  }
  .fa-kpi-value { color: #e4e6eb; }
  .fa-kpi-label { color: #6e7079; }
  .fa-kpi-foot { color: #8e9099; }

  // Pestañas
  .nav-tabs { background: #1f1f1f !important; border-bottom-color: #2c2c2c !important; }
  .nav-tabs .nav-link { color: #8e9099 !important; }
  .nav-tabs .nav-link:hover { color: #e4e6eb !important; }
  .nav-tabs .nav-link.active { color: $fa-primary !important; }

  // Botones outline: borde claro → oscuro
  .btn[class*="btn-outline-"],
  .btn[class*="btn-flat-"]:not(.btn-flat-primary) { border-color: #2c2c2c !important; }
  .btn.btn-outline-secondary:hover:not(:disabled) { background: #242424 !important; color: #e4e6eb !important; }

  .table {
    background: #1f1f1f !important;
    color: #d0d2d6 !important;
    thead th { background: #1f1f1f !important; color: #8e9099 !important; border-bottom-color: #2c2c2c !important; }
    tbody tr { background: #1f1f1f !important; }
    tbody tr td { border-top-color: #2c2c2c !important; color: #d0d2d6 !important; }
    tbody tr:hover { background: #242424 !important; }
    tbody tr.row-horas-extras { background: rgba(239, 68, 68, 0.12) !important; }
    tbody tr.row-horas-extras:hover { background: rgba(239, 68, 68, 0.18) !important; }
    strong { color: #e4e6eb !important; }
    .text-muted { color: #8e9099 !important; }
  }

  // Inputs, fechas y selects
  .form-group label { color: #8e9099; }
  .form-control,
  .date-input,
  input.date-input.flatpickr-input,
  .date-input.form-control {
    background: #161616 !important;
    background-color: #161616 !important;
    border-color: #2c2c2c !important;
    color: #d0d2d6 !important;
    &::placeholder { color: #6e7079; }
    &:focus { background: #161616 !important; border-color: $fa-primary !important; }
  }
  .v-select {
    .vs__dropdown-toggle { background: #161616 !important; border-color: #2c2c2c !important; }
    .vs__selected, .vs__search { color: #d0d2d6 !important; }
    .vs__search::placeholder { color: #6e7079; }
    .vs__open-indicator, .vs__clear { fill: #8e9099 !important; }
    .vs__dropdown-menu { background: #1f1f1f !important; border-color: #2c2c2c !important; color: #d0d2d6 !important; }
    .vs__dropdown-option { color: #d0d2d6; }
  }

  .resumen-anual-section { background: #161616 !important; border-color: #2c2c2c !important; }
  .resumen-anual-header small,
  .text-muted, small { color: #8e9099 !important; }
  h5, strong { color: #e4e6eb; }
  .badge.badge-light-secondary { color: #b4b7bd !important; }
}

.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.12) !important;
}

.bg-light-success {
  background-color: rgba(40, 199, 111, 0.12) !important;
}

.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.12) !important;
}

.bg-light-info {
  background-color: rgba(0, 207, 232, 0.12) !important;
}

.bg-light-danger {
  background-color: rgba(234, 84, 85, 0.12) !important;
}

.bg-light-secondary {
  background-color: rgba(108, 117, 125, 0.12) !important;
}

.row-horas-extras {
  background-color: rgba(234, 84, 85, 0.12) !important;
}

.tabla-hoy {
  border-left: 3px solid #28c76f;
}

#modal-justificar-falta .modal-body {
  min-height: 500px;
}

.resumen-anual-section {
  border-top: 3px solid #7367f0;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.resumen-anual-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.resumen-anual-icon {
  font-size: 24px;
  color: #7367f0;
  margin-right: 12px;
}

.dark-layout {
  .bg-light-primary {
    background-color: rgba(115, 103, 240, 0.2) !important;
  }

  .bg-light-success {
    background-color: rgba(40, 199, 111, 0.2) !important;
  }

  .bg-light-warning {
    background-color: rgba(255, 159, 67, 0.2) !important;
  }

  .bg-light-info {
    background-color: rgba(0, 207, 232, 0.2) !important;
  }

  .bg-light-danger {
    background-color: rgba(234, 84, 85, 0.2) !important;
  }

  .bg-light-secondary {
    background-color: rgba(108, 117, 125, 0.2) !important;
  }

  .row-horas-extras {
    background-color: rgba(234, 84, 85, 0.2) !important;
  }

  .resumen-anual-section {
    background: #242424 !important;
  }
}
</style>
